/*
 * slideshow-video-defer.js
 *
 * Defers slideshow video loading until the FIRST user interaction anywhere on
 * the page. On load, video slides show only their poster image (no video
 * network request). The first click / touchstart / scroll / keydown builds the
 * real <video> for EVERY deferred video slide on the page, then the listeners
 * detach. This is a single page-global one-time gate (not per-slide).
 *
 * Playback matches Dawn's previous behavior: autoplay + muted + loop +
 * playsinline + no controls.
 *
 * Purely additive. Guarded so multiple slideshow sections on one page bind the
 * listeners only once and never hydrate a container twice.
 */
(function () {
  'use strict';

  var INTERACTION_EVENTS = ['click', 'touchstart', 'scroll', 'keydown'];

  // Build the real <video> for a single deferred container.
  function hydrate(container) {
    if (!container || container.dataset.videoLoaded === 'true') return;
    container.dataset.videoLoaded = 'true';

    var sources;
    try {
      sources = JSON.parse(container.getAttribute('data-video-sources') || '[]');
    } catch (e) {
      sources = [];
    }
    if (!sources.length) return;

    var video = document.createElement('video');
    video.className = 'slideshow__video-el';
    // Match Dawn's prior playback attributes exactly.
    video.autoplay = true;
    video.loop = true;
    video.muted = true;
    video.setAttribute('muted', ''); // required for reliable autoplay
    video.playsInline = true;
    video.setAttribute('playsinline', '');
    video.controls = false;
    video.preload = 'auto';

    for (var i = 0; i < sources.length; i++) {
      var src = sources[i] && sources[i].src;
      if (!src) continue;
      var sourceEl = document.createElement('source');
      sourceEl.src = src;
      if (sources[i].type) sourceEl.type = sources[i].type;
      video.appendChild(sourceEl);
    }

    // Keep the poster visible until the first frame paints, then fade it out
    // to avoid any flash / CLS during handoff.
    video.addEventListener(
      'playing',
      function () {
        container.classList.add('is-playing');
      },
      { once: true }
    );

    container.appendChild(video);

    var playPromise = video.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      // Autoplay may be blocked in some contexts; poster simply remains.
      playPromise.catch(function () {});
    }
  }

  // Hydrate every deferred video slide currently in the DOM.
  function loadAll() {
    var containers = document.querySelectorAll('[data-slideshow-video-defer]');
    for (var i = 0; i < containers.length; i++) {
      hydrate(containers[i]);
    }
  }

  var LISTENER_OPTS = { once: true, passive: true };

  var fired = false;
  function onFirstInteraction() {
    if (fired) return;
    fired = true;
    // Detach all listeners — first interaction of ANY kind is one-time.
    for (var i = 0; i < INTERACTION_EVENTS.length; i++) {
      window.removeEventListener(INTERACTION_EVENTS[i], onFirstInteraction, LISTENER_OPTS);
    }
    loadAll();
  }

  function bindInteractionGate() {
    for (var i = 0; i < INTERACTION_EVENTS.length; i++) {
      window.addEventListener(INTERACTION_EVENTS[i], onFirstInteraction, LISTENER_OPTS);
    }
  }

  // Bind the page-global gate only once, even with multiple slideshow sections.
  if (!window.__rikumoSlideshowVideoDeferBound) {
    window.__rikumoSlideshowVideoDeferBound = true;

    if (window.Shopify && window.Shopify.designMode) {
      // In the theme editor, show videos immediately so merchants can preview,
      // and re-hydrate when a section is (re)loaded.
      loadAll();
      document.addEventListener('shopify:section:load', loadAll);
    } else {
      bindInteractionGate();
    }
  }
})();
